import { PermissionArrayItem, toast, useDeleteAssignUser } from '@lovoldsystem/authorization.client'
import {
  IClientUserWithAccess,
  IFacilityDto,
  IUserBasicDetails,
} from '@lovoldsystem/authorization.client/components/users/authorizationMapping.action'
import { IRole } from '@lovoldsystem/authorization.client/types/role'
import { Delete, Link, PersonAdd } from '@mui/icons-material'
import { Checkbox, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import { GridActionsCellItem, GridColumns, GridNativeColTypes } from '@mui/x-data-grid-pro'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { invalidateUserInviteCache, useUserInvite } from 'src/api/user-invites'
import { hasPermission } from 'src/components/common/Authorization'
import { DynamicTypography } from 'src/components/DynamicTextTypography'
import { permissionValue } from 'src/enums/permissionValue'
import { IUser } from 'src/generated-types'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import { getLocalFormattedDateTimeFromUTCString } from 'src/utils/function'
import { getUserDetail, UserDetail } from 'src/utils/getUserDetails'
import { invalidateCustomer, useGetCustomerUsersForAdmins } from '../../../api/customer'
import GenericTable from '../../../components/table/GenericTable'
import { FacilityTypes } from '../../../enums'
import { UserStatus } from '../../../enums/user-status'
import { useDialogCtx, useUserCtx } from '../../../hooks/context-hooks'
import InviteLink from './InviteLink'
import UserScopesColumn from './UserScopesColumn'
import {
  getCustomerUserUniqueRoleMappings,
  getRolesColumnSortValue,
  getScopeColumnSortValue,
  getValidUntilColumnSortValue,
} from './UserTable.action'

export interface UserTableProps {
  customerId: EntityId
  onRowClick?: (
    clientUserLinks: IClientUserWithAccess,
    user: IUserBasicDetails,
    services: Record<number, IFacilityDto[]>,
    directPermissions: PermissionArrayItem[],
  ) => void
  handleSelectCheckbox: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, row: IUser, data: IUser[]) => void
  selectedUsers: IUser[]
  searchText?: string
  roleFilters?: IRole[]
}

export const UserTable: React.FC<UserTableProps> = ({
  customerId,
  selectedUsers,
  onRowClick,
  handleSelectCheckbox,
  searchText,
  roleFilters,
}) => {
  const { t: translate } = useTranslation(['users', 'common'])
  const { data, refetch, isLoading } = useGetCustomerUsersForAdmins(
    customerId,
    true,
    searchText || '',
    roleFilters?.map(r => r.id),
  )
  const theme = useTheme()
  const { mutateAsync } = useDeleteAssignUser()
  const { showConfirmDialog, showDialog } = useDialogCtx()
  const { mutateAsync: inviteUsers } = useUserInvite(customerId)
  const { t } = useTranslation(['user-invite', 'admin-sidebar', 'common'])
  const { user } = useUserCtx()
  const canCopyInviteLink = useMemo(
    () =>
      hasPermission([permissionValue.ClientAdminPermission, permissionValue.SystemAdminPermission], user?.permission),
    [user],
  )

  const onInvite = async (email: string, status: UserStatus) => {
    showConfirmDialog(
      t(status == UserStatus.InComplete ? 'new-invite' : 'renew-invite', { ns: 'user-invite' }),
      t(status == UserStatus.InComplete ? 'new-invite-message' : 'renew-invite-message', { ns: 'user-invite' }),
      {
        acceptText: t('yes', { ns: 'common' }),
        cancelText: t('no', { ns: 'common' }),
      },
      async pop => {
        await inviteUsers({ email })
          .then(() => {
            invalidateUserInviteCache.useGetUserInvites(customerId)
            invalidateCustomer.getCustomerUsersForAdmins(customerId, true)
            toast.success(t('successfullyUpdated', { ns: 'common-api-message' }))
          })
          .catch(() => {
            toast.error(t('unExpectedError', { ns: 'common-api-message' }))
          })
        pop()
      },
    )
  }

  const copyInviteLink = (email: string, status: UserStatus) => {
    showDialog(InviteLink, {
      componentProps: { email, onResend: () => onInvite(email, status) },
      opts: {
        maxWidth: 'md',
        fullWidth: true,
        disableCloseButton: true,
      },
      sx: {
        '& .MuiDialogTitle-root': {
          paddingTop: '16px',
          paddingBottom: '0px',
        },
      },
    })
  }

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'id',
        headerName: '',
        type: 'actions',
        width: 25,
        sx: {
          paddingLeft: '0px',
        },
        renderCell: ({ row }) => {
          return (
            <Checkbox
              sx={{ padding: '0px' }}
              checked={selectedUsers.some(x => x.id === row.id)}
              onClick={e => handleSelectCheckbox(e, row as IUser, data || [])}
            />
          )
        },
      },
      {
        field: 'firstName',
        headerName: `${translate('fullName', { ns: 'users' })} / ${translate('email', { ns: 'users' })}`,
        flex: 1.25,
        renderCell: params => {
          const fullName = getUserDetail(params.row, UserDetail.FullName)
          return (
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <DynamicTypography variant="subtitle2" fontWeight={500} text={fullName} />
              <DynamicTypography variant="body2" fontWeight={400} text={params.row.email} />
            </div>
          )
        },
      },
      { field: 'phoneNumber', headerName: translate('phoneNumber', { ns: 'users' }), flex: 0.75 },
      {
        field: 'roles',
        headerName: translate('roles', { ns: 'access-control' }),
        flex: 1.5,
        sortable: true,
        valueGetter: ({ row }) => getRolesColumnSortValue(row, translate),
        renderCell: ({ row }) => {
          const roleBasedMappings = getCustomerUserUniqueRoleMappings(row)

          const directPermissionsMappings = (row?.clientUserMappings ?? []).filter(
            ul => ul.permissions != null && ul.permissions.length > 0,
          )
          if (
            (!roleBasedMappings || roleBasedMappings.length === 0) &&
            (!directPermissionsMappings || directPermissionsMappings?.length == 0)
          ) {
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography
                  ml={2}
                  variant="body1"
                  fontWeight={400}
                  color={getThemeColor(theme, MUIThemeColors.secondaryMain)}
                >
                  {translate('no-client-roles', { ns: 'access-control' })}
                </Typography>
              </div>
            )
          }

          const firstMapping = roleBasedMappings[0]
          const remainingMappings = roleBasedMappings.slice(1)

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {firstMapping && (
                <Chip
                  key={firstMapping?.clientUserLinkId}
                  style={{
                    background: `${getThemeColor(theme, MUIThemeColors.primaryMain)}55`,
                    color: getThemeColor(theme, MUIThemeColors.primaryDark),
                    marginLeft: 2,
                  }}
                  label={translate(firstMapping?.roleName || '', { ns: 'access-control' })}
                />
              )}

              {remainingMappings.length > 0 && (
                <Tooltip
                  title={
                    <>
                      {remainingMappings.map(mapping => (
                        <div key={mapping.clientUserLinkId}>
                          {translate(mapping?.roleName || '', { ns: 'access-control' })}
                        </div>
                      ))}
                    </>
                  }
                  arrow
                  placement="bottom"
                >
                  <Chip
                    style={{
                      background: `${getThemeColor(theme, MUIThemeColors.primaryMain)}55`,
                      color: getThemeColor(theme, MUIThemeColors.primaryDark),
                      cursor: 'pointer',
                    }}
                    label={`+${remainingMappings.length}`}
                  />
                </Tooltip>
              )}

              {directPermissionsMappings.length > 0 && (
                <Chip
                  style={{
                    background: `${getThemeColor(theme, MUIThemeColors.successMain)}55`,
                    color: getThemeColor(theme, MUIThemeColors.successDark),
                  }}
                  label={translate('plusDirectPermissions', { ns: 'access-control' })}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'scope',
        headerName: translate('scope', { ns: 'users' }),
        flex: 2,
        sortable: true,
        valueGetter: ({ row }) => getScopeColumnSortValue(row, translate),
        renderCell: ({ row, api }) => {
          const clientAdmin = row.clientUserMappings?.find(
            c => c.type == FacilityTypes.client && (c.mappingId == 0 || c.mappingId == null),
          )
          // `api` is unavailable when this cell is rendered outside the DataGrid (e.g. the mobile
          // card view), so guard against it to avoid crashing; the dropdown direction only matters
          // in the grid where the last row would otherwise open off-screen.
          const rowIndex = api?.getRowIndex(row.id) ?? -1
          const isLastRow = api ? rowIndex === api.getRowsCount() - 1 : false
          return (
            <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {clientAdmin != null ? (
                <Chip
                  label={translate('full-access-to-client', { ns: 'access-control' })}
                  sx={{
                    background: `${getThemeColor(theme, MUIThemeColors.primaryLight)}44`,
                    color: getThemeColor(theme, MUIThemeColors.primaryDark),
                    marginBottom: 0.5,
                    marginTop: 0.5,
                  }}
                  onDelete={() => {
                    showConfirmDialog(
                      translate('delete-confirm-direct-title', { ns: 'access-control' }),
                      translate('delete-confirm-direct-description', { ns: 'access-control' }),
                      {
                        acceptText: translate('yes', { ns: 'common' }),
                        cancelText: translate('no', { ns: 'common' }),
                      },
                      async pop => {
                        await mutateAsync(clientAdmin?.clientUserLinkId, {
                          onSuccess: () => {
                            pop()
                            refetch()
                          },
                        })
                      },
                    )
                  }}
                  deleteIcon={
                    <Delete style={{ color: getThemeColor(theme, MUIThemeColors.successDark), height: 19 }} />
                  }
                />
              ) : (
                <UserScopesColumn
                  scopes={row.scopes}
                  isLastRow={isLastRow}
                  onRowClick={(id, type) => {
                    showConfirmDialog(
                      translate('delete-confirm-role-title', { ns: 'access-control' }),
                      translate('delete-confirm-role-description', { ns: 'access-control' }),
                      {
                        acceptText: translate('yes', { ns: 'common' }),
                        cancelText: translate('no', { ns: 'common' }),
                      },
                      async pop => {
                        await mutateAsync(
                          row?.clientUserMappings.find(v => v.mappingId == id && v.type == type)?.clientUserLinkId,
                          {
                            onSuccess: () => {
                              pop()
                              refetch()
                            },
                          },
                        )
                      },
                    )
                  }}
                />
              )}
            </div>
          )
        },
      },
      {
        field: 'validUntil',
        headerName: translate('validUntil', { ns: 'certificate' }),
        flex: 0.75,
        sortable: true,
        type: 'number',
        headerAlign: 'left',
        valueGetter: ({ row }) => getValidUntilColumnSortValue(row),
        sortComparator: (v1, v2) => {
          if (v1 == null && v2 == null) return 0
          if (v1 == null) return 1
          if (v2 == null) return -1
          return (v1 as number) - (v2 as number)
        },
        renderCell: ({ row }) => {
          const validUntilDates = row.clientUserMappings?.map(m => m.validUntil).filter(date => date != null) || []
          const isIdentical = validUntilDates.every(date => date === validUntilDates[0])

          if (!isIdentical || validUntilDates.length === 0) {
            return (
              <Typography variant="body1" fontWeight={400} color={getThemeColor(theme, MUIThemeColors.secondaryMain)}>
                {translate('notSpecified', { ns: 'users' })}
              </Typography>
            )
          } else {
            const formattedDate = getLocalFormattedDateTimeFromUTCString(validUntilDates[0])
            return (
              <Typography variant="body1" fontWeight={400}>
                {formattedDate}
              </Typography>
            )
          }
        },
      },
      {
        field: 'status',
        headerName: translate('status', { ns: 'users' }),
        flex: 0.65,
        renderCell: ({ value }) => (
          <Chip
            label={
              value == UserStatus.Pending
                ? translate('invited', { ns: 'users' })
                : translate(UserStatus[value]?.toString(), { ns: 'users' })
            }
            sx={{
              background:
                value == UserStatus.Pending
                  ? `${getThemeColor(theme, MUIThemeColors.warningMain)}44`
                  : value == UserStatus.Complete
                  ? `${getThemeColor(theme, MUIThemeColors.successMain)}44`
                  : `${getThemeColor(theme, MUIThemeColors.errorMain)}44`,
              color:
                value == UserStatus.Pending
                  ? getThemeColor(theme, MUIThemeColors.warningDark)
                  : value == UserStatus.Complete
                  ? getThemeColor(theme, MUIThemeColors.successDark)
                  : getThemeColor(theme, MUIThemeColors.errorDark),
            }}
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions' as GridNativeColTypes,
        width: 60,
        getActions: params =>
          params.row.status == UserStatus.Pending
            ? [
                <GridActionsCellItem
                  key={params.id}
                  label={
                    params.row.status == UserStatus.Pending
                      ? translate('remind-invitation', { ns: 'user-invite' })
                      : translate('invite', { ns: 'common' })
                  }
                  icon={<PersonAdd sx={{ fontSize: 24 }} />}
                  onClick={() => onInvite(params.row?.email, params.row?.status)}
                  color="primary"
                  showInMenu
                />,
                ...(canCopyInviteLink
                  ? [
                      <GridActionsCellItem
                        key={`${params.id}-copy-link`}
                        label={translate('copy-invite-link', { ns: 'user-invite' })}
                        icon={<Link sx={{ fontSize: 24 }} />}
                        onClick={() => copyInviteLink(params.row?.email, params.row?.status)}
                        color="primary"
                        showInMenu
                      />,
                    ]
                  : []),
              ]
            : [],
      },
    ],
    [translate, selectedUsers, data, canCopyInviteLink],
  )

  const dataList = useMemo(() => {
    return data ? [...data].sort((a, b) => a.email.localeCompare(b.email, 'en', { sensitivity: 'base' })) : []
  }, [data])

  const onCellClick = useCallback(({ row, field, colDef }) => {
    if (field === 'actions' || field === 'roles' || field === 'status' || colDef.type === 'actions') {
      return
    }
    if (onRowClick) {
      const services = {
        [FacilityTypes.locality]: [],
        [FacilityTypes.serviceStation]: [],
        [FacilityTypes.storage]: [],
        [FacilityTypes.client]: [],
      } as Record<number, IFacilityDto[]>
      let directPermissions = [] as PermissionArrayItem[]
      row.clientUserMappings.forEach(mapping => {
        const mappingType =
          mapping.type == FacilityTypes.localityGroup
            ? FacilityTypes.locality
            : mapping.type == FacilityTypes.serviceStationGroup
            ? FacilityTypes.serviceStation
            : mapping.type
        services[mappingType ?? FacilityTypes.locality].push({
          id: mapping.mappingId,
          type: mapping.type,
        })
        directPermissions = directPermissions.concat(mapping.permissions || [])
      })

      onRowClick(
        row.clientUserMappings,
        {
          id: row.id,
          firstName: row.firstName,
          lastName: row.lastName,
          email: row.email,
        },
        services,
        directPermissions,
      )
    }
  }, [])

  return (
    <GenericTable
      id="users-table"
      data={dataList}
      getRowHeight={() => 'auto'}
      loading={isLoading}
      columns={columns}
      enableToolbar={true}
      onCellClick={onCellClick}
      autoHeight={false}
      onRowClick={() => {}} // for enable row cursor pointer
      height={window.innerHeight - 230 + 'px'}
      enableUXFeatures
    />
  )
}
